import { FoodfluenceLocation } from '@foodfluence/shared';
import { parseCalendarDateTimeToUtc } from '@foodfluence/shared';
import { getBrowserTimezone } from 'utils/timezoneUtils';

export interface TimezoneInfo {
  value: string;  // IANA timezone (e.g. "America/New_York")
  display: string;  // Abbreviated format (e.g. "EST")
  /** Where the zone came from. 'browser' means the venue's timezone was NOT
   * available — callers writing a visit time for a located campaign must treat
   * that as an error, or the stored instant is wrong whenever the creator's
   * device zone differs from the venue's (privacy browsers even report "UTC"). */
  source: 'location' | 'browser';
}

export const getTimezone = (locationId: string | undefined, locations: FoodfluenceLocation[]): TimezoneInfo => {
  let timezone: string;
  let source: TimezoneInfo['source'];
  const location = locationId && locations ? locations.find(loc => loc.docId === locationId) : undefined;
  if (location?.timezone) {
    timezone = location.timezone;
    source = 'location';
  } else {
    timezone = getBrowserTimezone();
    source = 'browser';
  }

  // Get abbreviated format (EST, PST, etc)
  const display = new Date().toLocaleTimeString('en-US', {
    timeZone: timezone,
    timeZoneName: 'short'
  }).split(' ').pop() || timezone;

  return {
    value: timezone,
    display,
    source
  };
};

export const convertToLocalTime = (selectedDate: Date, selectedTime: string, timezone: string): TimeConversionResult => {
  const [time, modifier] = selectedTime.split(' ');
  let [hours, minutes] = time.split(':').map(Number);

  if (modifier === 'PM' && hours < 12) hours += 12;
  else if (modifier === 'AM' && hours === 12) hours = 0;

  // The picked wall clock as a browser-local Date: its date/time FIELDS equal the
  // picked values, so browser-local formatting of it reproduces the picked label.
  const dateTime = new Date(selectedDate);
  dateTime.setHours(hours, minutes, 0, 0);

  // The actual instant: the picked wall clock interpreted in `timezone`, resolved
  // with the shared DST-exact utility. (The previous implementation sampled the
  // zone offset at the browser-local instant — a single-pass trick that lands an
  // hour off around DST transitions whenever the browser and venue zones differ,
  // silently storing a wrong scheduledCollabDate.)
  const localTime = parseCalendarDateTimeToUtc(
    selectedDate.getFullYear(),
    selectedDate.getMonth() + 1,
    selectedDate.getDate(),
    selectedTime,
    timezone
  );

  return { localTime, dateTime };
};

interface TimeConversionResult {
  localTime: Date;
  dateTime: Date;
}
